/**
 * Maps the internal paths of the route tree (English words: `/categories/jablotron-100-plus/detectors`,
 * `/products/ja-150m`) to the public, language-prefixed paths with localised words
 * (`/de/kategorien/jablotron-100-plus/melder`, `/de/produkte/ja-150m`) and back, per PDR-0019 and PDR-0020.
 *
 * Used by the router rewrite in the browser, by the build script that writes the page manifest and by the
 * Koa server, so it stays environment-neutral: no window, document or React, no import.meta.glob and no
 * `~/` path alias. Its only data is src/data/slugs.json (imported relatively): the English entries of
 * that table are the slugs of the internal paths, so the catalog itself is not needed. A subcategory page
 * belongs to the category in front of the underscore of its id (`2_3` → `2`), which a unit test checks
 * against catalog.json.
 *
 * The URL layer owns the language prefix, the section words and the category and subcategory slugs.
 * Product codes and blog slugs are taken over as they are (a product code is only lower-cased, see
 * parseLocalisedPath); whether such a product or article exists is decided by the page inventory and the
 * route loaders, which answer 404 for a missing one.
 */
import slugsJson from '../../data/slugs.json';
import { type LanguageCode, languageCodes } from '../../texts/languages';
import { productSlug } from '../catalog/paths';
import { type SectionKey, sectionKeys, type SlugEntry, type SlugTable } from './slugs';

export type ParsedLocalisedPath =
  | { kind: 'page'; lang: LanguageCode; internalPath: string }
  /** A permanent (301) move: legacy slug, product code in another case, trailing slash or `/de` without it. */
  | { kind: 'redirect'; location: string }
  | { kind: 'unknown' };

export type LocalisedPaths = {
  /**
   * The public path of an internal path in a language, e.g. ('de', '/products/JA-150M?x=1#faq') →
   * '/de/produkte/ja-150m?x=1#faq'. Query and hash pass through unchanged. Words and slugs it doesn't
   * know (`/ui-kit`, an unknown category) are kept as they are under the prefix, so the result of such a
   * path is a URL that parseLocalisedPath answers with `unknown`.
   */
  toLocalisedPath: (lang: LanguageCode, internalPath: string) => string;
  /**
   * The page a public path points to. `/de/` is the home page; `/de` and any other path ending in a slash
   * redirect to the path without it (with it for `/de/`), legacy slugs and product codes that are not in
   * their slug form redirect to the current path. Anything else is `unknown`: no or an unknown language
   * prefix, English or another language's words under a prefix (`/cs/categories/…`), unknown slugs,
   * empty segments, extra segments.
   */
  parseLocalisedPath: (path: string) => ParsedLocalisedPath;
};

const unknown: ParsedLocalisedPath = { kind: 'unknown' };

/** The part from the first `?` or `#` on, which both directions pass through unchanged. */
function splitSuffix(path: string): [pathname: string, suffix: string] {
  const end = path.search(/[?#]/);

  return end === -1 ? [path, ''] : [path.slice(0, end), path.slice(end)];
}

/** The category id of a subcategory page id, e.g. "1_3" → "1". */
const categoryOf = (subcategoryId: string): string => subcategoryId.split('_')[0];

type Match = { id: string; legacy: boolean };

/** The entry whose current slug is `slug`, else the one with `slug` in its legacy array. */
function matchSlug(entries: [string, SlugEntry][], slug: string): Match | undefined {
  const current = entries.find(([, entry]) => entry.slug === slug);
  if (current) {
    return { id: current[0], legacy: false };
  }
  const legacy = entries.find(([, entry]) => entry.legacy.includes(slug));

  return legacy && { id: legacy[0], legacy: true };
}

/** Builds the two mappings over a slug table; the exported functions use src/data/slugs.json. */
export function createLocalisedPaths(table: SlugTable): LocalisedPaths {
  const internal = table.en;
  const isLanguage = (value: string): value is LanguageCode =>
    (languageCodes as readonly string[]).includes(value) && value in table;
  const isSectionKey = (value: string): value is SectionKey => (sectionKeys as readonly string[]).includes(value);
  const subcategoriesOf = (slugs: SlugTable[string], categoryId: string) =>
    Object.entries(slugs.subcategories).filter(([id]) => categoryOf(id) === categoryId);

  /** Internal paths may name a category or subcategory by its English slug (also a legacy one) or its id. */
  const findInternal = (entries: [string, SlugEntry][], segment: string): string | undefined => {
    const needle = segment.toLowerCase();

    return matchSlug(entries, needle)?.id ?? entries.find(([id]) => id.toLowerCase() === needle)?.[0];
  };

  function localiseSegments(lang: LanguageCode, segments: string[]): string[] {
    const [section, ...rest] = segments;
    if (!isSectionKey(section)) {
      return segments;
    }
    const slugs = table[lang];
    const localised = [slugs.sections[section], ...rest];

    if (section === 'products' && rest.length === 1) {
      return [localised[0], productSlug(rest[0])];
    }
    if (section !== 'categories' || rest.length === 0 || rest.length > 2) {
      return localised;
    }

    const categoryId = findInternal(Object.entries(internal.categories), rest[0]);
    if (categoryId === undefined) {
      return localised;
    }
    localised[1] = slugs.categories[categoryId]?.slug ?? rest[0];
    if (rest.length === 2) {
      const subcategoryId = findInternal(subcategoriesOf(internal, categoryId), rest[1]);
      if (subcategoryId !== undefined) {
        localised[2] = slugs.subcategories[subcategoryId]?.slug ?? rest[1];
      }
    }

    return localised;
  }

  function toLocalisedPath(lang: LanguageCode, internalPath: string): string {
    if (!isLanguage(lang)) {
      throw new Error(`toLocalisedPath: no slugs for language "${lang}"`);
    }
    const [pathname, suffix] = splitSuffix(internalPath);
    if (!pathname.startsWith('/')) {
      throw new Error(`toLocalisedPath: "${internalPath}" is not an absolute path`);
    }
    const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const segments = trimmed === '/' ? [] : trimmed.slice(1).split('/');

    return `/${[lang, ...localiseSegments(lang, segments)].join('/')}${segments.length === 0 ? '/' : ''}${suffix}`;
  }

  /** The internal path of the segments after the prefix, and whether a legacy or non-slug form was used. */
  function matchSegments(lang: LanguageCode, segments: string[]): { path: string; moved: boolean } | undefined {
    if (segments.length === 0) {
      return { path: '/', moved: false };
    }
    if (segments.some((segment) => segment === '')) {
      return undefined;
    }
    const slugs = table[lang];
    const [word, ...rest] = segments;
    const section = sectionKeys.find((key) => slugs.sections[key] === word);

    switch (section) {
      case 'products':
        if (rest.length > 1) {
          return undefined;
        }

        return rest.length === 0
          ? { path: '/products', moved: false }
          : { path: `/products/${productSlug(rest[0])}`, moved: productSlug(rest[0]) !== rest[0] };
      case 'blog':
        return rest.length > 1 ? undefined : { path: ['', 'blog', ...rest].join('/'), moved: false };
      case 'compatibility':
      case 'search':
        return rest.length === 0 ? { path: `/${section}`, moved: false } : undefined;
      case 'categories': {
        if (rest.length === 0 || rest.length > 2) {
          return undefined;
        }
        const category = matchSlug(Object.entries(slugs.categories), rest[0]);
        if (!category) {
          return undefined;
        }
        const path = [`/categories/${internal.categories[category.id].slug}`];
        let moved = category.legacy;
        if (rest.length === 2) {
          const subcategory = matchSlug(subcategoriesOf(slugs, category.id), rest[1]);
          if (!subcategory) {
            return undefined;
          }
          path.push(internal.subcategories[subcategory.id].slug);
          moved ||= subcategory.legacy;
        }

        return { path: path.join('/'), moved };
      }
      default:
        return undefined;
    }
  }

  function parseLocalisedPath(path: string): ParsedLocalisedPath {
    const [pathname, suffix] = splitSuffix(path);
    const [empty, lang, ...segments] = pathname.split('/');
    if (empty !== '' || lang === undefined || !isLanguage(lang)) {
      return unknown;
    }
    if (segments.length === 0) {
      return { kind: 'redirect', location: `/${lang}/${suffix}` };
    }
    // '/de/' (one empty segment) is the home page; any other path ending in a slash redirects to the path
    // without it.
    const home = segments.length === 1 && segments[0] === '';
    const trailingSlash = !home && segments[segments.length - 1] === '';
    if (home || trailingSlash) {
      segments.pop();
    }
    const matched = matchSegments(lang, segments);
    if (!matched) {
      return unknown;
    }

    return trailingSlash || matched.moved
      ? { kind: 'redirect', location: toLocalisedPath(lang, matched.path) + suffix }
      : { kind: 'page', lang, internalPath: matched.path + suffix };
  }

  return { toLocalisedPath, parseLocalisedPath };
}

export const { toLocalisedPath, parseLocalisedPath } = createLocalisedPaths(slugsJson as unknown as SlugTable);
